import Component from '@glimmer/component'
import { StudentEventsReport } from 'teacher-ui-client/components/reporting/reading-eggspress/read-aloud-fluency/student/events/report'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { service } from '@ember/service'
import { Product } from 'teacher-ui-client/utils/products'
import { trackedFunction } from 'reactiveweb/function'
import { type BaseReport, type BaseReportMetaPaging, toRequestScopeStudent } from 'teacher-ui-client/services/reporting'
import type { NormalizedInterval } from 'date-fns'
import type Student from 'teacher-ui-client/models/student'
import { BasePageSelector as PageSelector } from 'teacher-ui-client/components/base/page-selector'
import { keepLatest } from 'reactiveweb/keep-latest'
import { use } from 'ember-resources'
import ContentTile from 'teacher-ui-client/components/tiles/content'

export interface ReportData extends BaseReport {
  events: {
    title: string
    created_at: string
    lexile_formatted?: string
    assignment_uuid: string
    reviewed_at: Nullable<string>
    score: Nullable<number>
    words_per_minute: Nullable<number>
    comments: Nullable<string>
    review_id: Nullable<string>
  }[]
  meta: BaseReportMetaPaging
}

interface ReportLoaderSignature {
  Args: {
    student: Nullable<Student>
    period: NormalizedInterval
    sort: string
    pageNumber: number
    perPage: number
  }
}

export class ReportLoader extends Component<ReportLoaderSignature> {
  @service reporting!: ReportingService

  scheduledReload?: ReturnType<typeof setTimeout>

  fetchReport = trackedFunction(this, async () => {
    // if a report reload was pending, clear it now that the report has changed.
    clearTimeout(this.scheduledReload)

    if (!this.args.student?.id) return undefined

    const url = this.reporting.buildGravityUrl({
      product: Product.REX,
      requestScope: toRequestScopeStudent(this.args.student.id),
      reportName: 'student-dashboard/read-aloud-fluency/events',
    })
    const query = this.reporting.buildQuery({
      period: this.args.period,
      page: {
        number: this.args.pageNumber,
        size: this.args.perPage,
      },
      sort: this.args.sort,
    })
    const report = await this.reporting.fetchReport<ReportData>(url, query)
    return report.events.length ? report : null
  })

  @use
  latestReport = keepLatest({
    value: () => this.fetchReport.value,
    when: () => this.fetchReport.isPending,
  })

  /**
   * The teacher is able to modify the underlying data (reviews) that drive this report. Additionally, it can take some
   * time for review changes to appear in the report, as reporting data from student-events is eventually consistent.
   *
   * Reload the report after 10 seconds to ensure it's up to date.
   */
  reloadReport = () => {
    void this.fetchReport.retry()
    this.scheduledReload = setTimeout(() => void this.fetchReport.retry(), 10000)
  }

  willDestroy() {
    super.willDestroy()
    clearTimeout(this.scheduledReload)
  }

  <template>
    {{#if this.latestReport}}
      {{#if @student}}
        <ContentTile>
          <StudentEventsReport
            class="overflow-x-auto print:block print:max-h-[unset] print:overflow-visible"
            @isReloading={{this.fetchReport.isPending}}
            @reportData={{this.latestReport}}
            @student={{@student}}
            @reloadReport={{this.reloadReport}}
          />
          <PageSelector
            class="mx-2 my-3"
            @pageCount={{this.latestReport.meta.total_pages}}
            @itemCount={{this.latestReport.meta.total_entries}}
          />
        </ContentTile>
      {{/if}}
    {{/if}}
  </template>
}
